import { useState, useRef, useEffect } from 'react';
import { Upload, FileText, X, CheckCircle } from 'lucide-react';
import { documentsAPI } from '../services/api';
import { useHaptic } from '../hooks/useHaptic';
import './DocumentUploader.css';

function DocumentUploader({ onUploadComplete, onCancel, isOpen }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const haptic = useHaptic();

  // Блокируем скролл страницы когда компонент открыт
  useEffect(() => {
    if (isOpen) {
      // Блокируем скролл
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
    }
    
    return () => {
      // Восстанавливаем скролл
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  // Сбрасываем состояние при открытии/закрытии модального окна
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setSelectedFile(null);
        setUploading(false);
        setUploadProgress(0);
        setError(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const acceptedFormats = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword'
  ];

  const handleFileSelect = (file) => {
    setError(null);

    if (!acceptedFormats.includes(file.type)) {
      setError('Неподдерживаемый формат файла. Поддерживаются: PDF, PNG, JPEG, DOCX');
      haptic.error();
      return;
    }

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Файл слишком большой. Максимальный размер: 50MB');
      haptic.error();
      return;
    }

    setSelectedFile(file);
    haptic.itemSelect();
    
    setTimeout(() => {
      handleUploadFile(file);
    }, 100);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUploadFile = async (file) => {
    if (!file) return;

    try {
      setUploading(true);
      setUploadProgress(0);
      haptic.buttonPressImportant();

      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await documentsAPI.upload(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      console.log('✅ Документ загружен:', response.data);
      
      haptic.success();
      
      setTimeout(() => {
        if (onUploadComplete) {
          onUploadComplete(response.data.document);
        }
      }, 500);

    } catch (err) {
      console.error('❌ Ошибка загрузки документа:', err);
      setError(err.response?.data?.error || 'Ошибка при загрузке документа');
      haptic.error();
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
    setUploadProgress(0);
    haptic.delete();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="document-uploader">
      {/* Основной контент */}
      <div className="uploader-content">
        {/* Заголовок */}
        <div className="uploader-header">
          <h2>Загрузка документа</h2>
          <p className="uploader-subtitle">Поддерживаются PDF, Word, изображения</p>
        </div>

        {/* Кнопка выбора файла или информация о выбранном файле */}
        {!selectedFile ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.docx,.doc"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              id="document-file-input"
            />
            <label htmlFor="document-file-input" className="file-select-button">
              <Upload size={20} />
              Выбрать файл
            </label>
          </>
        ) : (
          <div className="selected-file">
            <div className="file-info">
              <div className="file-icon">
                <FileText size={24} />
              </div>
              <div className="file-details">
                <h4>{selectedFile.name}</h4>
                <p>{formatFileSize(selectedFile.size)}</p>
              </div>
              {!uploading && uploadProgress !== 100 && (
                <button 
                  className="remove-file-button"
                  onClick={handleRemoveFile}
                  aria-label="Удалить файл"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}

            {uploadProgress === 100 && (
              <div className="upload-success">
                <CheckCircle size={20} />
                <span>Документ загружен!</span>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}
      </div>

      {/* Кнопка внизу - зафиксирована */}
      <div className="uploader-footer">
        <button
          className="cancel-button"
          onClick={() => {
            haptic.buttonPress();
            onCancel();
          }}
          disabled={uploading}
        >
          {uploadProgress === 100 ? 'Закрыть' : 'Отмена'}
        </button>
      </div>
    </div>
  );
}

export default DocumentUploader;
